import { AwsRum, AwsRumConfig } from 'aws-rum-web';

let awsRumInstance: AwsRum | null = null;

// Static file extensions to filter out from telemetry
const STATIC_ASSET_REGEX = /\.(css|png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot|map)(\?.*)?$/i;

// Keys that must be masked / redacted for privacy & security
const SENSITIVE_KEYS = ['password', 'token', 'secret', 'authorization', 'auth', 'card', 'cvv', 'ssn', 'cookie'];

// Debounce map for user actions (prevents spamming events on rapid double clicks)
const lastActionTimes = new Map<string, number>();
const ACTION_DEBOUNCE_MS = 500;

/**
 * Sanitize event metadata to prevent logging sensitive user information
 */
export function sanitizeMetadata(data: Record<string, any>): Record<string, any> {
  if (!data || typeof data !== 'object') return {};
  const sanitized: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_KEYS.some(sk => key.toLowerCase().includes(sk))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      try {
        sanitized[key] = sanitizeMetadata(value);
      } catch {
        sanitized[key] = '[Object]';
      }
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Configuration options loaded from environment variables (with gl-ui defaults)
 */
const RUM_CONFIG = {
  applicationId: process.env.REACT_APP_AWS_RUM_APP_ID || '6ac73fbe-ca67-4c00-9e26-6b4868f71a83',
  applicationVersion: process.env.REACT_APP_AWS_RUM_APP_VERSION || '1.0.0',
  applicationRegion: process.env.REACT_APP_AWS_RUM_REGION || 'us-east-1',
  identityPoolId: process.env.REACT_APP_AWS_RUM_IDENTITY_POOL_ID || 'us-east-1:3dcd1aa8-bd45-47d2-952d-70b188e69afc',
  isEnabled: process.env.REACT_APP_AWS_RUM_ENABLED !== 'false',
};

/**
 * Initialize AWS CloudWatch RUM client
 */
export function initRum(): AwsRum | null {
  if (awsRumInstance) {
    return awsRumInstance;
  }

  // Only initialize if enabled and required credentials/IDs are present
  if (!RUM_CONFIG.isEnabled) {
    console.info('[AWS RUM] RUM monitoring is disabled via environment configuration.');
    return null;
  }

  if (!RUM_CONFIG.applicationId || !RUM_CONFIG.identityPoolId) {
    console.warn(
      '[AWS RUM] Missing REACT_APP_AWS_RUM_APP_ID or REACT_APP_AWS_RUM_IDENTITY_POOL_ID. RUM client not started.'
    );
    return null;
  }

  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1, // 100% of sessions
      identityPoolId: RUM_CONFIG.identityPoolId,
      endpoint: `https://dataplane.rum.${RUM_CONFIG.applicationRegion}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
      signing: true,
    };

    awsRumInstance = new AwsRum(
      RUM_CONFIG.applicationId,
      RUM_CONFIG.applicationVersion,
      RUM_CONFIG.applicationRegion,
      config
    );

    console.info('[AWS RUM] CloudWatch RUM initialized successfully on gl-ui.');
    return awsRumInstance;
  } catch (error) {
    console.error('[AWS RUM] Failed to initialize CloudWatch RUM:', error);
    return null;
  }
}

/**
 * Get current AWS RUM instance
 */
export function getRumInstance(): AwsRum | null {
  return awsRumInstance;
}

/**
 * General logging helper: info
 */
export function logInfo(message: string, context?: Record<string, any>): void {
  try {
    if (!awsRumInstance) return;
    const sanitized = context ? sanitizeMetadata(context) : {};
    awsRumInstance.recordEvent('app_info', { message, ...sanitized });
  } catch (err) {
    console.error('[AWS RUM] Failed to log info:', err);
  }
}

/**
 * General logging helper: warning
 */
export function logWarn(message: string, context?: Record<string, any>): void {
  try {
    if (!awsRumInstance) return;
    const sanitized = context ? sanitizeMetadata(context) : {};
    awsRumInstance.recordEvent('app_warn', { message, ...sanitized });
  } catch (err) {
    console.error('[AWS RUM] Failed to log warn:', err);
  }
}

/**
 * General logging helper: error
 */
export function logError(error: Error | string, context?: Record<string, any>): void {
  recordCustomError(error, context);
}

/**
 * Record custom caught error/exception to AWS RUM
 */
export function recordCustomError(
  error: Error | string,
  context?: Record<string, any>
): void {
  try {
    if (!awsRumInstance) return;

    if (typeof error === 'string') {
      awsRumInstance.recordError(new Error(error));
    } else {
      awsRumInstance.recordError(error);
    }

    if (context) {
      awsRumInstance.recordEvent('custom_error_context', sanitizeMetadata(context));
    }
  } catch (err) {
    console.error('[AWS RUM] Failed to record error:', err);
  }
}

/**
 * Record custom business or user interaction event to AWS RUM
 */
export function recordCustomEvent(
  eventType: string,
  eventData: Record<string, any>
): void {
  try {
    if (!awsRumInstance) return;
    const sanitized = sanitizeMetadata(eventData);
    awsRumInstance.recordEvent(eventType, sanitized);
  } catch (err) {
    console.error('[AWS RUM] Failed to record custom event:', err);
  }
}

/**
 * Record user action steps (button clicks, form submits, filter changes, modals)
 * Filters static noise, debounces rapid clicks, and logs formatted output to DevTools console.
 */
export function logUserAction(
  action: string,
  details?: Record<string, any>
): void {
  try {
    const now = Date.now();
    const lastTime = lastActionTimes.get(action) || 0;
    if (now - lastTime < ACTION_DEBOUNCE_MS) {
      return; // Debounced rapid duplicate action
    }
    lastActionTimes.set(action, now);

    const timestamp = new Date().toISOString();
    const sanitizedDetails = details ? sanitizeMetadata(details) : {};
    const path = typeof window !== 'undefined' ? window.location.pathname : '';

    // Formatted color-coded output in browser DevTools Console
    console.log(
      `%c[UserAction]%c [${action}]`,
      'background: #0284c7; color: #fff; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
      'color: #0369a1; font-weight: bold;',
      { path, timestamp, ...sanitizedDetails }
    );

    // Send custom telemetry event to AWS CloudWatch RUM
    if (awsRumInstance) {
      awsRumInstance.recordEvent('user_action', {
        action,
        path,
        timestamp,
        ...sanitizedDetails,
      });
    }
  } catch (err) {
    console.error('[AWS RUM] Failed to record user action:', err);
  }
}

/**
 * Record a page route view in AWS RUM
 */
export function recordPageView(pagePath: string): void {
  try {
    if (!awsRumInstance) return;
    // Skip static assets if invoked accidentally
    if (STATIC_ASSET_REGEX.test(pagePath)) return;
    awsRumInstance.recordPageView(pagePath);
  } catch (err) {
    console.error('[AWS RUM] Failed to record page view:', err);
  }
}

const rum = {
  initRum,
  getRumInstance,
  logInfo,
  logWarn,
  logError,
  logUserAction,
  recordCustomError,
  recordCustomEvent,
  recordPageView,
  sanitizeMetadata,
};

export default rum;
